import { Injectable, signal } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { firebaseConfig, vapidKey } from '../config/firebase.config';

/**
 * NotificationService - Handles Firebase Cloud Messaging (FCM) for push notifications
 *
 * Features:
 * - Initializes Firebase app and messaging
 * - Requests notification permissions from user
 * - Retrieves FCM registration token
 * - Listens to foreground messages
 * - Uses signals for reactive state management
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  // Firebase messaging instance
  private messaging: Messaging | null = null;

  // Reactive state signals
  public fcmToken = signal<string | null>(null);
  public permissionGranted = signal<boolean>(false);
  public error = signal<string | null>(null);

  constructor() {
    this.initializeFirebase();
  }

  /**
   * Initializes Firebase app and messaging service
   */
  private initializeFirebase(): void {
    try {
      // Initialize Firebase
      const app = initializeApp(firebaseConfig);

      // Get messaging instance (only available in browsers)
      if (typeof window !== 'undefined' && 'Notification' in window) {
        this.messaging = getMessaging(app);
        console.log('✅ Firebase initialized successfully');
      } else {
        console.warn('⚠️ Push notifications not supported in this environment');
      }
    } catch (error) {
      console.error('❌ Error initializing Firebase:', error);
      this.error.set('Failed to initialize Firebase');
    }
  }

  /**
   * Requests notification permission and retrieves FCM token
   * @returns Promise<string | null> - FCM token or null if permission denied
   */
  async requestPermission(): Promise<string | null> {
    if (!this.messaging) {
      console.warn('⚠️ Messaging not initialized');
      return null;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        console.log('✅ Notification permission granted');
        this.permissionGranted.set(true);

        // Get FCM registration token
        const token = await getToken(this.messaging, {
          vapidKey: vapidKey
        });

        if (token) {
          console.log('🔑 FCM Registration Token:', token);
          this.fcmToken.set(token);
          this.error.set(null);

          // TODO: Send this token to your backend to store it
          // await this.apiService.saveDeviceToken(token);

          return token;
        } else {
          console.warn('⚠️ No registration token available');
          this.error.set('No registration token available');
          return null;
        }
      } else if (permission === 'denied') {
        console.warn('❌ Notification permission denied');
        this.permissionGranted.set(false);
        this.error.set('Notification permission denied by user');
        return null;
      } else {
        console.warn('⚠️ Notification permission dismissed');
        this.error.set('Notification permission dismissed');
        return null;
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      this.error.set('Error requesting notification permission');
      return null;
    }
  }

  /**
   * Listens to incoming messages when app is in foreground
   * Messages received when app is in background are handled by the service worker
   */
  listenToMessages(): void {
    if (!this.messaging) {
      console.warn('⚠️ Messaging not initialized');
      return;
    }

    onMessage(this.messaging, (payload) => {
      console.log('📩 Message received (foreground):', payload);

      // Extract notification data
      const notificationTitle = payload.notification?.title || 'New Notification';
      const notificationOptions = {
        body: payload.notification?.body || '',
        icon: payload.notification?.icon || '/icons/icon-192x192.png',
        badge: '/icons/icon-96x96.png',
        data: payload.data,
        tag: payload.data?.['tag'] || 'default',
        requireInteraction: false
      };

      // Show notification using Notification API
      if (Notification.permission === 'granted') {
        new Notification(notificationTitle, notificationOptions);
      }

      // You can also emit this to a subject/signal if you want to handle it in components
      // this.messageReceived.next(payload);
    });

    console.log('👂 Listening to foreground messages...');
  }

  /**
   * Checks current notification permission status
   * @returns string - 'granted', 'denied', or 'default'
   */
  getPermissionStatus(): NotificationPermission {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'denied';
  }

  /**
   * Checks if notifications are supported in this browser
   * @returns boolean
   */
  isNotificationSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Gets the current FCM token (reactive signal)
   */
  getCurrentToken(): string | null {
    return this.fcmToken();
  }
}